import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ExamResultDetails() {

  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [backendUrl, setBackendUrl] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const activeRole =
    localStorage.getItem("activeRole") || "Student";

  const userId =
    localStorage.getItem("userId");

  useEffect(() => {

    const script = document.createElement("script");

    script.src = "/config.js";

    script.onload = () => {

      if (window.env?.BACKEND_URL) {
        setBackendUrl(window.env.BACKEND_URL);
      }

    };

    document.body.appendChild(script);

  }, []);

  useEffect(() => {

    if (!backendUrl || !attemptId) return;

    loadResult();

  }, [backendUrl, attemptId]);

  const loadResult = async () => {

    try {

      setLoading(true);
      setError("");

      const res = await axios.get(
        `${backendUrl}/results/attempt/${attemptId}`,
        {
          params: {
            viewerRole: activeRole,
            userId
          }
        }
      );

      setData(res.data);

    } catch (err) {

      console.error(
        "Detailed result error:",
        err
      );

      setError(
        err.response?.data?.error ||
        "Unable to load result."
      );

    } finally {

      setLoading(false);

    }
  };

  const downloadPDF = () => {

    if (!data) return;

    const doc = new jsPDF();

    doc.setFontSize(16);

    doc.text(
      "Exam Result",
      14,
      18
    );

    doc.setFontSize(11);

    doc.text(
      `Student: ${data.attempt.Student_Name}`,
      14,
      30
    );

    doc.text(
      `Exam: ${data.attempt.Exam_Name}`,
      14,
      37
    );

    doc.text(
      `Attempt: ${data.attempt.Attempt_Number}`,
      14,
      44
    );

    doc.text(
      `Marks: ${data.summary.Marks_Obtained} / ${data.summary.Total_Marks}`,
      14,
      51
    );

    doc.text(
      `Percentage: ${data.summary.Percentage}%`,
      14,
      58
    );

    doc.text(
      `Correct: ${data.summary.Correct_Answers}`,
      14,
      65
    );

    doc.text(
      `Wrong: ${data.summary.Wrong_Answers}`,
      80,
      65
    );

    doc.text(
      `Unattempted: ${data.summary.Unattempted_Questions}`,
      130,
      65
    );

    autoTable(doc, {

      startY: 75,

      head: [[
        "#",
        "Question",
        "Student Answer",
        "Correct Answer",
        "Marks",
        "Result"
      ]],

      body: data.questions.map((q, index) => [

        index + 1,

        q.Question_Text,

        q.Student_Answer || "-",

        q.Correct_Answer || "-",

        q.Marks_Obtained,

        q.Question_Result

      ]),

      styles: {
        fontSize: 7,
        cellPadding: 2
      },

      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 50 },
        2: { cellWidth: 40 },
        3: { cellWidth: 40 },
        4: { cellWidth: 12 },
        5: { cellWidth: 18 }
      }
    });

    doc.save(
      `Exam_Result_${data.attempt.Student_Name}_${data.attempt.Attempt_ID}.pdf`
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <p>Loading result...</p>
      </div>
    );
  }

  if (error) {

    return (
      <div style={styles.container}>

        <button
          onClick={() => navigate("/dashboard")}
          style={styles.backButton}
        >
          ← Back
        </button>

        <div style={styles.error}>
          {error}
        </div>

      </div>
    );
  }

  if (!data) return null;

  const summary = data.summary;

  return (

    <div style={styles.container}>

      <div style={styles.headerRow}>

        <button
          onClick={() => navigate(-1)}
          style={styles.backButton}
        >
          ← Back
        </button>

        <h2 style={styles.title}>
          Exam Result
        </h2>

        {activeRole !== "Student" && (
          <button
            onClick={downloadPDF}
            style={styles.pdfButton}
          >
            Download PDF
          </button>
        )}

      </div>

      {/* EXAM INFORMATION */}

      <div style={styles.infoCard}>

        <div>
          <strong>Student</strong>
          <br />
          {data.attempt.Student_Name}
        </div>

        <div>
          <strong>Exam</strong>
          <br />
          {data.attempt.Exam_Name}
        </div>

        <div>
          <strong>Attempt</strong>
          <br />
          {data.attempt.Attempt_Number}
        </div>

      </div>

      {/* SUMMARY */}

      <div style={styles.summaryGrid}>

        <Summary
          label="Total Questions"
          value={summary.Total_Questions}
        />

        <Summary
          label="Attempted"
          value={summary.Attempted_Questions}
        />

        <Summary
          label="Correct"
          value={summary.Correct_Answers}
          good
        />

        <Summary
          label="Wrong"
          value={summary.Wrong_Answers}
          bad
        />

        <Summary
          label="Unattempted"
          value={summary.Unattempted_Questions}
        />

        <Summary
          label="Marks"
          value={`${summary.Marks_Obtained} / ${summary.Total_Marks}`}
        />

        <Summary
          label="Percentage"
          value={`${summary.Percentage}%`}
        />

      </div>

      {/* QUESTIONS */}

      <div style={styles.questionsCard}>

        <h3>
          Question-wise Result
        </h3>

        {data.questions.map((q, index) => {

          const result =
            q.Question_Result;

          const resultColor =
            result === "Correct"
              ? "green"
              : result === "Wrong"
              ? "red"
              : "#777";

          return (

            <div
              key={q.Question_ID}
              style={{
                ...styles.question,
                borderLeft:
                  `5px solid ${resultColor}`
              }}
            >

              <div style={styles.questionTitle}>
                Q{index + 1}. {q.Question_Text}
              </div>

              <div>
                <strong>
                  Student Answer:
                </strong>

                <span
                  style={{
                    color: resultColor,
                    fontWeight: 600,
                    marginLeft: 8
                  }}
                >
                  {q.Student_Answer || "Not Attempted"}
                </span>
              </div>

              <div>

                <strong>
                  Correct Answer:
                </strong>

                <span
                  style={{
                    color: "green",
                    fontWeight: 600,
                    marginLeft: 8
                  }}
                >
                  {q.Correct_Answer}
                </span>

              </div>

              <div style={styles.questionFooter}>

                <span>
                  Marks: {q.Marks_Obtained} / {q.Marks}
                </span>

                <span
                  style={{
                    color: resultColor,
                    fontWeight: 700
                  }}
                >
                  {result}
                </span>

              </div>

            </div>

          );

        })}

      </div>

    </div>
  );
}

function Summary({
  label,
  value,
  good,
  bad
}) {

  return (

    <div
      style={{
        ...styles.summaryCard,
        borderTop:
          good
            ? "4px solid green"
            : bad
            ? "4px solid red"
            : "4px solid #C70013"
      }}
    >

      <div style={styles.summaryLabel}>
        {label}
      </div>

      <div style={styles.summaryValue}>
        {value}
      </div>

    </div>
  );
}

const styles = {

  container: {
    maxWidth: 1200,
    margin: "20px auto",
    padding: 20,
    fontFamily: "Arial"
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: 15,
    marginBottom: 20
  },

  title: {
    color: "#C70013",
    flex: 1
  },

  backButton: {
    background: "#666",
    color: "#fff",
    border: "none",
    padding: "9px 16px",
    borderRadius: 6,
    cursor: "pointer"
  },

  pdfButton: {
    background: "#C70013",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: 6,
    cursor: "pointer"
  },

  infoCard: {
    display: "grid",
    gridTemplateColumns:
      "repeat(3, 1fr)",
    gap: 20,
    background: "#f7f7f7",
    padding: 20,
    borderRadius: 8,
    marginBottom: 20
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
    marginBottom: 25
  },

  summaryCard: {
    background: "#fff",
    padding: 15,
    borderRadius: 8,
    boxShadow:
      "0 2px 8px rgba(0,0,0,.12)"
  },

  summaryLabel: {
    color: "#666",
    fontSize: 13
  },

  summaryValue: {
    fontSize: 22,
    fontWeight: 700,
    marginTop: 5
  },

  questionsCard: {
    background: "#fff"
  },

  question: {
    padding: 15,
    marginBottom: 12,
    background: "#fafafa",
    borderRadius: 6
  },

  questionTitle: {
    fontWeight: 600,
    marginBottom: 10
  },

  questionFooter: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 10
  },

  error: {
    color: "#C70013",
    marginTop: 30,
    fontWeight: 600
  }
};